import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { ArtifactData } from '@/types';

interface ArtifactContextType {
  // Current artifacts state
  artifacts: Map<string, ArtifactData>;
  
  // Active artifact management
  activeArtifactId: string | null;
  setActiveArtifactId: (id: string | null) => void;
  
  // Artifact operations
  updateArtifact: (id: string, content: string, type?: string) => void;
  addArtifact: (artifact: ArtifactData) => void;
  removeArtifact: (id: string) => void;
  clearArtifacts: () => void;
  toggleArtifactInclusion: (id: string) => void;
  
  // Get formatted artifacts for API calls
  getArtifactsForApi: (assistantId?: string, tools?: string[]) => string;
  getSelectiveArtifactsForApi: (options?: {
    mode?: 'active' | 'recent' | 'selected' | 'all';
    limit?: number;
    assistantId?: string;
    tools?: string[];
  }) => string;
  
  // Panel state
  isPanelOpen: boolean;
  setPanelOpen: (open: boolean) => void;
}

const ArtifactContext = createContext<ArtifactContextType | undefined>(undefined);

interface ArtifactProviderProps {
  children: ReactNode;
  assistantId?: string;
}

const STORAGE_KEY_PREFIX = 'aitana-artifacts';

// Helper functions for localStorage
const getStorageKey = (assistantId?: string): string => {
  return assistantId ? `${STORAGE_KEY_PREFIX}-${assistantId}` : STORAGE_KEY_PREFIX;
};

const loadArtifactsFromStorage = (assistantId?: string): Map<string, ArtifactData> => {
  try {
    const storageKey = getStorageKey(assistantId);
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const data = JSON.parse(stored);
      return new Map(Object.entries(data));
    }
  } catch (error) {
    console.warn('Failed to load artifacts from localStorage:', error);
  }
  return new Map();
};

const saveArtifactsToStorage = (artifacts: Map<string, ArtifactData>, assistantId?: string) => {
  try {
    const storageKey = getStorageKey(assistantId);
    const data = Object.fromEntries(artifacts);
    localStorage.setItem(storageKey, JSON.stringify(data));
  } catch (error) {
    console.warn('Failed to save artifacts to localStorage:', error);
  }
};

export const ArtifactProvider: React.FC<ArtifactProviderProps> = ({ children, assistantId }) => {
  const [artifacts, setArtifacts] = useState<Map<string, ArtifactData>>(new Map());
  const [activeArtifactId, setActiveArtifactId] = useState<string | null>(null);
  const [isPanelOpen, setPanelOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load artifacts from localStorage on mount or when assistantId changes
  useEffect(() => {
    const loadedArtifacts = loadArtifactsFromStorage(assistantId);
    setArtifacts(loadedArtifacts);
    setActiveArtifactId(null); // Reset active artifact when switching assistants
    setIsLoaded(true);
  }, [assistantId]);

  // Save artifacts to localStorage whenever they change (but not on initial load)
  useEffect(() => {
    if (isLoaded) {
      saveArtifactsToStorage(artifacts, assistantId);
    }
  }, [artifacts, isLoaded, assistantId]);

  // Update an existing artifact's content and optionally type
  const updateArtifact = useCallback((id: string, content: string, type?: string) => {
    setArtifacts(prev => {
      const updated = new Map(prev);
      const existing = updated.get(id);
      if (existing) {
        updated.set(id, { 
          ...existing, 
          content,
          ...(type && { type })
        });
      }
      return updated;
    });
  }, []);

  // Add a new artifact
  const addArtifact = useCallback((artifact: ArtifactData) => {
    setArtifacts(prev => {
      const updated = new Map(prev);
      // Add timestamp if not provided
      const artifactWithTimestamp = {
        ...artifact,
        createdAt: artifact.createdAt || Date.now(),
        includeInApi: artifact.includeInApi !== undefined ? artifact.includeInApi : true
      };
      updated.set(artifact.id, artifactWithTimestamp);
      return updated;
    });
    
    // Auto-select the new artifact if none is selected
    if (!activeArtifactId) {
      setActiveArtifactId(artifact.id);
    }
    
    // Don't auto-open panel - let user click to open
    // This was causing panel to open on every page load
  }, [activeArtifactId]);

  // Remove an artifact
  const removeArtifact = useCallback((id: string) => {
    setArtifacts(prev => {
      const updated = new Map(prev);
      updated.delete(id);
      
      // If we removed the active artifact, select another one or clear selection
      if (activeArtifactId === id) {
        const remaining = Array.from(updated.keys());
        setActiveArtifactId(remaining.length > 0 ? remaining[0] : null);
      }
      
      return updated;
    });
  }, [activeArtifactId]);

  // Clear all artifacts
  const clearArtifacts = useCallback(() => {
    setArtifacts(new Map());
    setActiveArtifactId(null);
    setPanelOpen(false);
  }, []);

  // Toggle artifact inclusion in API calls
  const toggleArtifactInclusion = useCallback((id: string) => {
    setArtifacts(prev => {
      const updated = new Map(prev);
      const artifact = updated.get(id);
      if (artifact) {
        updated.set(id, {
          ...artifact,
          includeInApi: !artifact.includeInApi
        });
      }
      return updated;
    });
  }, []);

  // Format artifacts for API calls - if assistantId is provided, load from that assistant's storage
  const getArtifactsForApi = useCallback((targetAssistantId?: string, tools?: string[]): string => {
    // Check if the artifacts tool is enabled
    const hasArtifactsTool = tools?.includes('artifacts') ?? false;
    if (!hasArtifactsTool) {
      return '';
    }

    // Use the specified assistantId or fall back to the current provider's assistantId
    const effectiveAssistantId = targetAssistantId || assistantId;
    
    // If we're asking for the same assistant as the current context, use current artifacts
    const artifactsToUse = effectiveAssistantId === assistantId 
      ? artifacts 
      : loadArtifactsFromStorage(effectiveAssistantId);

    if (artifactsToUse.size === 0) {
      return '';
    }

    const artifactStrings = Array.from(artifactsToUse.values()).map(artifact => 
      `<artifact id="${artifact.id}" title="${artifact.title}" type="${artifact.type}">${artifact.content}</artifact>`
    );

    return 'Current artifacts:\n' + artifactStrings.join('\n');
  }, [artifacts, assistantId]);

  // Selective artifact sending for optimized API calls
  const getSelectiveArtifactsForApi = useCallback((options?: {
    mode?: 'active' | 'recent' | 'selected' | 'all';
    limit?: number;
    assistantId?: string;
    tools?: string[];
  }): string => {
    const { 
      mode = 'active', 
      limit = 1, 
      assistantId: targetAssistantId, 
      tools 
    } = options || {};

    // Check if the artifacts tool is enabled
    const hasArtifactsTool = tools?.includes('artifacts') ?? false;
    if (!hasArtifactsTool) {
      return '';
    }

    // Use the specified assistantId or fall back to the current provider's assistantId
    const effectiveAssistantId = targetAssistantId || assistantId;
    
    // If we're asking for the same assistant as the current context, use current artifacts
    const artifactsToUse = effectiveAssistantId === assistantId 
      ? artifacts 
      : loadArtifactsFromStorage(effectiveAssistantId);

    if (artifactsToUse.size === 0) {
      return '';
    }

    let selectedArtifacts: ArtifactData[] = [];

    switch (mode) {
      case 'active':
        // Send only the currently active artifact
        if (activeArtifactId) {
          const activeArtifact = artifactsToUse.get(activeArtifactId);
          if (activeArtifact) {
            selectedArtifacts = [activeArtifact];
          }
        }
        break;

      case 'recent':
        // Send the most recent artifacts (up to limit)
        const sortedByTime = Array.from(artifactsToUse.values())
          .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
          .slice(0, limit);
        selectedArtifacts = sortedByTime;
        break;

      case 'selected':
        // Send only artifacts marked for inclusion
        selectedArtifacts = Array.from(artifactsToUse.values())
          .filter(artifact => artifact.includeInApi !== false);
        break;

      case 'all':
        // Send all artifacts (fallback to original behavior)
        selectedArtifacts = Array.from(artifactsToUse.values());
        break;
    }

    if (selectedArtifacts.length === 0) {
      return '';
    }

    const artifactStrings = selectedArtifacts.map(artifact => 
      `<artifact id="${artifact.id}" title="${artifact.title}" type="${artifact.type}">${artifact.content}</artifact>`
    );

    return 'Current artifacts:\n' + artifactStrings.join('\n');
  }, [artifacts, assistantId, activeArtifactId]);

  const value: ArtifactContextType = {
    artifacts,
    activeArtifactId,
    setActiveArtifactId,
    updateArtifact,
    addArtifact,
    removeArtifact,
    clearArtifacts,
    toggleArtifactInclusion,
    getArtifactsForApi,
    getSelectiveArtifactsForApi,
    isPanelOpen,
    setPanelOpen,
  };

  return (
    <ArtifactContext.Provider value={value}>
      {children}
    </ArtifactContext.Provider>
  );
};

export const useArtifacts = (): ArtifactContextType => {
  const context = useContext(ArtifactContext);
  if (context === undefined) {
    throw new Error('useArtifacts must be used within an ArtifactProvider');
  }
  return context;
};